import { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { KeyRound, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { AUTH_MODE_OPTIONS, McpAuthMode, McpServer } from './mcpServerRegistry';
import { useMcpServerDraft } from './useMcpServerDraft';

interface McpServerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing server to edit; omit to create. */
  server?: McpServer | null;
  onSaved: (server: McpServer) => void;
}

/**
 * Create or edit an org-level MCP server (create-in-place from the agent
 * builder / service editor, or from the Integrations page). Draft, test and
 * save logic live in useMcpServerDraft, shared with onboarding.
 */
export default function McpServerDialog({ open, onOpenChange, server, onSaved }: McpServerDialogProps) {
  const {
    isEdit,
    draft,
    setField,
    updateConnectionDetails,
    reset,
    connectionChanged,
    test: handleTest,
    testing,
    testResult,
    testError,
    save,
    saving,
    saveError,
    startOAuth,
    startingOAuth,
  } = useMcpServerDraft(server);

  useEffect(() => {
    if (open) reset(server);
  }, [open, server, reset]);

  const oauthPending = draft.auth_mode === 'oauth' && (!isEdit || server?.auth_mode !== 'oauth');

  const handleSave = async () => {
    if (oauthPending) {
      await startOAuth('integrations');
      return;
    }
    const saved = await save();
    if (saved) {
      onSaved(saved);
      onOpenChange(false);
    }
  };

  const selectedAuthOption = AUTH_MODE_OPTIONS.find((option) => option.value === draft.auth_mode);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden min-w-0">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit MCP Server' : 'Connect MCP Server'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Changes apply to every agent and service bound to this server.'
              : 'Connect a server that speaks the Model Context Protocol once for your organization, then give any agent or service a subset of its tools.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mcp-server-name">Name</Label>
            <Input
              id="mcp-server-name"
              value={draft.name}
              placeholder="e.g. Product API"
              onChange={(e) => setField({ name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mcp-server-description">Description</Label>
            <Textarea
              id="mcp-server-description"
              value={draft.description}
              placeholder="What these tools are and do — e.g. 'Operates the Acme workspace the user is signed into: projects, members, and billing.'"
              rows={3}
              maxLength={2000}
              onChange={(e) => setField({ description: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Shown to your agents so they understand these tools operate your product for the
              current user. Optional but recommended.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mcp-server-url">Server URL</Label>
            <Input
              id="mcp-server-url"
              value={draft.url}
              placeholder="https://mcp.yourcompany.com/mcp"
              onChange={(e) => updateConnectionDetails({ url: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Authentication</Label>
            <Select
              value={draft.auth_mode}
              onValueChange={(value) => updateConnectionDetails({ auth_mode: value as McpAuthMode })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select authentication mode" />
              </SelectTrigger>
              <SelectContent>
                {AUTH_MODE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedAuthOption && (
              <p className="text-xs text-muted-foreground">{selectedAuthOption.helper}</p>
            )}
          </div>

          {draft.auth_mode === 'oauth' && (
            <div className="rounded-lg border p-3 space-y-2">
              {isEdit && server?.auth_mode === 'oauth' && server.oauth ? (
                <>
                  <div className="flex items-center gap-2 flex-wrap text-sm">
                    <Badge
                      variant="outline"
                      className={`text-xs font-normal ${
                        server.oauth.status === 'active' ? 'text-green-700 border-green-300' : 'text-amber-600 border-amber-300'
                      }`}
                    >
                      {server.oauth.status === 'active' ? 'Signed in' : 'Needs re-authorization'}
                    </Badge>
                    {server.oauth.connected_by_email && (
                      <span className="text-xs text-muted-foreground">as {server.oauth.connected_by_email}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {server.oauth.status === 'active'
                      ? 'The stored sign-in is used for every call. Sign in again to switch the account it acts as.'
                      : 'The stored sign-in stopped working (revoked or expired). Sign in again to restore the connection.'}
                  </p>
                  <Button type="button" variant="outline" size="sm" disabled={startingOAuth} onClick={() => startOAuth('integrations')}>
                    {startingOAuth && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                    {server.oauth.status === 'active' ? 'Sign in again' : 'Re-authorize'}
                  </Button>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  You will be sent to {draft.name.trim() || 'your product'} to sign in, then straight back here. Nothing
                  is saved until sign-in succeeds.
                </p>
              )}
            </div>
          )}

          {draft.auth_mode === 'api_key' && (
            <div className="space-y-2">
              <Label htmlFor="mcp-server-api-key">
                {isEdit && server?.has_credential && server.auth_mode === 'api_key'
                  ? 'Replace API key'
                  : 'API key'}
              </Label>
              <Input
                id="mcp-server-api-key"
                type="password"
                value={draft.api_key}
                placeholder={
                  isEdit && server?.has_credential && server.auth_mode === 'api_key'
                    ? 'Leave blank to keep the stored key'
                    : 'Your MCP server API key'
                }
                autoComplete="new-password"
                onChange={(e) => updateConnectionDetails({ api_key: e.target.value })}
              />
              {isEdit && server?.has_credential && (
                <p className="text-xs text-muted-foreground">
                  A key is stored for this server. It is never shown again; enter a new one to
                  rotate it.
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Button type="button" variant="outline" size="sm" disabled={testing || oauthPending} onClick={handleTest}>
              {testing && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {testing ? 'Testing...' : 'Test connection'}
            </Button>
            {testError && <p className="text-xs text-red-500">{testError}</p>}
            {testResult && (
              <p className="text-xs text-muted-foreground break-words [overflow-wrap:anywhere]">
                {testResult.tools.length === 0
                  ? 'Connection succeeded, but the server exposed no tools.'
                  : `Connection succeeded: ${testResult.tools.length} tool${testResult.tools.length === 1 ? '' : 's'} discovered: ${testResult.tools
                      .slice(0, 8)
                      .map((tool) => tool.name)
                      .join(', ')}${testResult.tools.length > 8 ? ', …' : ''}`}
              </p>
            )}
          </div>

          {connectionChanged && (
            <p className="text-xs text-muted-foreground">
              Saving connects to the server and stores its discovered tools.
            </p>
          )}
          {saveError && <p className="text-xs text-red-500">{saveError}</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving || startingOAuth}>
            {(saving || startingOAuth) && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {oauthPending && !saving && !startingOAuth && <KeyRound className="mr-1.5 h-3.5 w-3.5" />}
            {oauthPending ? `Sign in with ${draft.name.trim() || 'your product'}` : isEdit ? 'Save changes' : 'Connect server'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
